/**
 * Centralized Input Validation Service
 * Provides secure validation for all user inputs across the application
 */

import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import DOMPurify from 'dompurify';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  constructor() {}

  /**
   * Sanitize HTML input to prevent XSS attacks
   */
  sanitizeHtml(input: string): string {
    if (!input) return '';
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });
  }

  /**
   * Sanitize and validate text input
   */
  sanitizeText(input: string, maxLength: number = 255): string {
    if (!input) return '';
    
    // Remove HTML tags and malicious content
    const sanitized = this.sanitizeHtml(input);
    
    // Trim whitespace and limit length
    return sanitized.trim().substring(0, maxLength);
  }

  /**
   * Validate email format with RFC 5322 compliance
   */
  static emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      
      return emailRegex.test(control.value) ? null : { invalidEmail: true };
    };
  }

  /**
   * Validate phone number format (international)
   */
  static phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      // Remove all non-digit characters
      const cleanPhone = control.value.replace(/\D/g, '');
      
      // Check if it's a valid international format
      const phoneRegex = /^(\+|00)?[1-9]\d{6,14}$/;
      
      return phoneRegex.test(control.value) ? null : { invalidPhone: true };
    };
  }

  /**
   * Validate strong password requirements
   */
  static passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const value = control.value;
      const errors: any = {};

      // Minimum 8 characters
      if (value.length < 8) {
        errors.minLength = true;
      }

      // Maximum 128 characters (prevent DoS)
      if (value.length > 128) {
        errors.maxLength = true;
      }

      // At least one lowercase letter
      if (!/[a-z]/.test(value)) {
        errors.requiresLowercase = true;
      }

      // At least one uppercase letter
      if (!/[A-Z]/.test(value)) {
        errors.requiresUppercase = true;
      }

      // At least one number
      if (!/\d/.test(value)) {
        errors.requiresNumber = true;
      }

      // At least one special character
      if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)) {
        errors.requiresSpecial = true;
      }

      // Check for common passwords
      const commonPasswords = [
        'password', '123456', 'password123', 'admin', 'qwerty',
        'letmein', 'welcome', 'monkey', '1234567890'
      ];
      
      if (commonPasswords.includes(value.toLowerCase())) {
        errors.commonPassword = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  /**
   * Validate parcel description (prevent malicious content)
   */
  static parcelDescriptionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const value = control.value.trim();
      
      // Check for minimum length
      if (value.length < 10) {
        return { minLength: { requiredLength: 10, actualLength: value.length } };
      }

      // Check for maximum length
      if (value.length > 500) {
        return { maxLength: { requiredLength: 500, actualLength: value.length } };
      }

      // Check for suspicious patterns (potential XSS/injection)
      const maliciousPatterns = [
        /<script/i,
        /javascript:/i,
        /vbscript:/i,
        /on\w+\s*=/i,
        /<iframe/i,
        /<object/i,
        /<embed/i,
        /data:text\/html/i
      ];

      for (const pattern of maliciousPatterns) {
        if (pattern.test(value)) {
          return { maliciousContent: true };
        }
      }

      return null;
    };
  }

  /**
   * Validate weight input (positive number, reasonable limits)
   */
  static weightValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const weight = parseFloat(control.value);
      
      if (isNaN(weight)) {
        return { invalidNumber: true };
      }

      if (weight <= 0) {
        return { mustBePositive: true };
      }

      if (weight > 1000) { // 1000kg max
        return { tooHeavy: { max: 1000, actual: weight } };
      }

      return null;
    };
  }

  /**
   * Validate Congo city names
   */
  static congoDestinationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const validCities = ['kinshasa', 'lubumbashi'];
      const city = control.value.toLowerCase().trim();
      
      return validCities.includes(city) ? null : { invalidDestination: true };
    };
  }

  /**
   * Validate file upload (type and size)
   */
  static fileValidator(allowedTypes: string[], maxSizeMB: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const file = control.value;
      
      if (!(file instanceof File)) {
        return { invalidFile: true };
      }

      // Check file type
      const fileType = file.type.toLowerCase();
      const isValidType = allowedTypes.some(type => 
        fileType.includes(type.toLowerCase())
      );
      
      if (!isValidType) {
        return { invalidFileType: { allowed: allowedTypes, actual: fileType } };
      }

      // Check file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        return { 
          fileTooLarge: { 
            maxSizeMB, 
            actualSizeMB: Math.round(file.size / (1024 * 1024) * 100) / 100 
          } 
        };
      }

      return null;
    };
  }

  /**
   * Rate limiting validator (prevent spam)
   */
  static rateLimitValidator(lastSubmission: Date | null, minIntervalMs: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!lastSubmission) return null;
      
      const now = new Date();
      const timeDiff = now.getTime() - lastSubmission.getTime();
      
      if (timeDiff < minIntervalMs) {
        const remainingMs = minIntervalMs - timeDiff;
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        
        return { 
          rateLimited: { 
            remainingSeconds,
            message: `Please wait ${remainingSeconds} seconds before submitting again.`
          } 
        };
      }

      return null;
    };
  }

  /**
   * Sanitize and validate search query
   */
  sanitizeSearchQuery(query: string): string {
    if (!query) return '';
    
    // Remove HTML and trim
    let sanitized = this.sanitizeHtml(query).trim();
    
    // Remove potential SQL injection patterns
    const sqlPatterns = [
      /[';"*%<>{}[\]|]/g,
      /(union|select|insert|update|delete|drop|create|alter|exec|execute)/gi
    ];
    
    sqlPatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
    
    // Limit length
    return sanitized.substring(0, 100);
  }

  /**
   * Validate tracking code format
   */
  static trackingCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      // Befret tracking code format: BF-YYYYMMDD-XXXX
      const trackingRegex = /^BF-\d{8}-[A-Z0-9]{4}$/;
      
      return trackingRegex.test(control.value.toUpperCase()) ? null : { invalidTrackingCode: true };
    };
  }

  /**
   * Get user-friendly error messages
   */
  getErrorMessage(controlName: string, errors: ValidationErrors): string {
    const errorMessages: { [key: string]: { [key: string]: string } } = {
      email: {
        required: 'Email is required',
        invalidEmail: 'Please enter a valid email address'
      },
      password: {
        required: 'Password is required',
        minLength: 'Password must be at least 8 characters long',
        maxLength: 'Password cannot exceed 128 characters',
        requiresLowercase: 'Password must contain at least one lowercase letter',
        requiresUppercase: 'Password must contain at least one uppercase letter',
        requiresNumber: 'Password must contain at least one number',
        requiresSpecial: 'Password must contain at least one special character',
        commonPassword: 'This password is too common, please choose a stronger one'
      },
      phone: {
        required: 'Phone number is required',
        invalidPhone: 'Please enter a valid phone number'
      },
      description: {
        required: 'Description is required',
        minLength: 'Description must be at least 10 characters long',
        maxLength: 'Description cannot exceed 500 characters',
        maliciousContent: 'Description contains prohibited content'
      },
      weight: {
        required: 'Weight is required',
        invalidNumber: 'Please enter a valid number',
        mustBePositive: 'Weight must be greater than 0',
        tooHeavy: 'Weight cannot exceed 1000kg'
      },
      destination: {
        required: 'Destination is required',
        invalidDestination: 'Please select a valid destination (Kinshasa or Lubumbashi)'
      },
      file: {
        invalidFile: 'Please select a valid file',
        invalidFileType: 'File type not allowed',
        fileTooLarge: 'File size exceeds the maximum limit'
      },
      trackingCode: {
        required: 'Tracking code is required',
        invalidTrackingCode: 'Please enter a valid tracking code (format: BF-YYYYMMDD-XXXX)'
      }
    };

    const controlErrors = errorMessages[controlName] || {};
    const firstErrorKey = Object.keys(errors)[0];
    
    return controlErrors[firstErrorKey] || `Invalid ${controlName}`;
  }
}